import React, { useContext, useEffect, useRef, useState } from 'react';
import { CircularProgress, styled, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
    StyledAddBasketTitle,
    StyledCameraIcon,
    StyledColumnBox,
    StyledDeleteLink,
    StyledFarmTitle,
    StyledGreenButton,
    StyledIconButton,
    StyledImage,
    StyledImageBox,
    StyledMediumTypography,
    StyledModalButtons,
    StyledNormalTypography,
    StyledRowBox,
    StyledWhiteButton
} from '../../theme/StyledElements';
import cameraIcon from '../../assets/camera.svg';
import { getProducer, updateFarm } from '../../services/cloudFirestore';
import { UserContext } from '../../providers/UserProvider';
import { useNavigate } from 'react-router-dom';
import { InputComponent } from '../../components/InputComponent';
import { InputAreaField } from '../../components/InputAreaField';
import { Producteurs } from '../../models/Producteurs';
import { AddressComponent } from '../../components/AddressComponent';
import { Coordinates } from '../../models/Coordinates';
import { useMountedRef } from '../../hooks/useMountedRef';
import { getImageKitUrlFrom, isPhoneNumberValidate } from '../../utils';

const StyledBox = styled(StyledColumnBox)(() => ({
    alignItems: 'left',
    marginBottom: '60px'
}));

const StyledBasketBox = styled(StyledBox)(() => ({
    marginTop: '40px',
    maxWidth: '745px',
    marginLeft: '10px'
}));

export function ModifyFarm(): React.ReactElement {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const mounted = useMountedRef();
    const user = useContext(UserContext);
    const [values, setValues] = useState<Producteurs>({
        idProducteur: undefined,
        mail: '',
        nom: '',
        prenom: '',
        nomFerme: '',
        telephone: '',
        adresse: '',
        infoFerme: '',
        illustrationUrl: undefined,
        panierRecuperationMethode: '',
        collections: undefined,
        stripeId: undefined,
        coordinates: undefined
    });
    const [wordCount, setWordCount] = useState<number>(0);
    const [descriptionCount, setDescriptionCount] = useState<number>(0);
    const [disabled, setDisabled] = useState<boolean>(true);
    const [basketPhoto, setBasketPhoto] = useState<string>();
    const basketPhotoFile = useRef<HTMLInputElement>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [isTelephoneInvalid, setIsTelephoneInvalid] = useState<boolean>(false);

    useEffect(() => {
        if (user && user.idProducteur && mounted.current) {
            getProducer(user.idProducteur).then((updatedUser) => {
                if (updatedUser && mounted.current) {
                    setValues({
                        idProducteur: updatedUser.idProducteur,
                        mail: updatedUser.mail,
                        nom: updatedUser.nom,
                        prenom: updatedUser.prenom,
                        nomFerme: updatedUser.nomFerme,
                        telephone: updatedUser.telephone,
                        adresse: updatedUser.adresse,
                        infoFerme: updatedUser.infoFerme,
                        illustrationUrl: updatedUser.illustrationUrl,
                        panierRecuperationMethode: updatedUser.panierRecuperationMethode,
                        coordinates: updatedUser.coordinates
                    });
                    if (typeof updatedUser.illustrationUrl === 'string' && mounted.current) {
                        setBasketPhoto(updatedUser.illustrationUrl);
                    }
                    if (updatedUser.panierRecuperationMethode && mounted.current) {
                        setWordCount(updatedUser.panierRecuperationMethode.length);
                    }
                    if (updatedUser.infoFerme && mounted.current) {
                        setDescriptionCount(updatedUser.infoFerme.length);
                    }
                }
            });
        }
    }, []);

    const onTextFieldChange = (state: string, value: string | number | File): void => {
        setValues({ ...values, [state]: value });
    };

    useEffect(() => {
        if (values.telephone) {
            if (!isPhoneNumberValidate(values.telephone)) {
                setIsTelephoneInvalid(true);
            } else {
                setIsTelephoneInvalid(false);
            }
        }
    }, [values.telephone]);

    useEffect(() => {
        if (
            values.nom &&
            values.prenom &&
            values.nomFerme &&
            values.telephone &&
            values.adresse &&
            values.coordinates &&
            !isTelephoneInvalid
        ) {
            setDisabled(false);
        } else {
            setDisabled(true);
        }
    }, [values]);

    const triggerBasketPhotoUpload = (): void => {
        if (basketPhotoFile && basketPhotoFile.current) {
            basketPhotoFile.current.click();
        }
    };

    const updateBasketPhoto = (event: React.ChangeEvent<HTMLInputElement>): void => {
        if (event.target.files) {
            setBasketPhoto(URL.createObjectURL(event.target.files[0]));
            onTextFieldChange('illustrationUrl', event.target.files[0]);
        }
    };

    const removeBasketPhoto = (): void => {
        setBasketPhoto('');
        onTextFieldChange('illustrationUrl', '');
    };

    const publishFarm = async (): Promise<void> => {
        setLoading(true);
        if (user && values) {
            const data = values;
            data.idProducteur = user.idProducteur;
            await updateFarm(data);
            setLoading(false);
            navigate('/view-farm');
        }
    };

    return (
        <StyledBasketBox>
            <StyledNormalTypography variant="h2">{t('myFarm')}</StyledNormalTypography>
            <StyledFarmTitle variant="body1">{t('generalInformation')}</StyledFarmTitle>
            <StyledAddBasketTitle variant="caption">{t('lastName')}*</StyledAddBasketTitle>
            <InputComponent
                type="text"
                name="nom"
                placeholder={t('lastName')}
                value={values?.nom}
                onTextChange={(value: string): void => onTextFieldChange('nom', value)}
                width="315px"
            />
            <StyledAddBasketTitle variant="caption">{t('firstName')}*</StyledAddBasketTitle>
            <InputComponent
                type="text"
                name="prenom"
                placeholder={t('firstName')}
                value={values?.prenom}
                onTextChange={(value: string): void => onTextFieldChange('prenom', value)}
                width="315px"
            />
            <StyledAddBasketTitle variant="caption">{t('nameOfFarm')}*</StyledAddBasketTitle>
            <InputComponent
                type="text"
                name="nomFerme"
                placeholder={t('nameOfFarm')}
                value={values?.nomFerme}
                onTextChange={(value: string): void => onTextFieldChange('nomFerme', value)}
                width="645px"
            />
            <StyledFarmTitle variant="body1">{t('contactInformation')}</StyledFarmTitle>
            <StyledAddBasketTitle variant="caption">{t('telephone')}*</StyledAddBasketTitle>
            <InputComponent
                type="text"
                name="telephone"
                placeholder={t('telephone')}
                value={values?.telephone}
                onTextChange={(value: string): void => onTextFieldChange('telephone', value)}
                width="315px"
            />
            {isTelephoneInvalid && (
                <Typography
                    variant="subtitle2"
                    sx={{ color: 'red', marginTop: '-11px', fontWeight: 400 }}>
                    {t('invalidPhoneNumber')}
                </Typography>
            )}
            <StyledAddBasketTitle variant="caption">{t('email')}*</StyledAddBasketTitle>
            <StyledNormalTypography variant="body2" sx={{ marginTop: '10px' }}>
                {values?.mail}
            </StyledNormalTypography>
            <StyledNormalTypography variant="body2">
                {t('contactSupportToChangeEmail')}
            </StyledNormalTypography>
            <StyledFarmTitle variant="body1">{t('basketRecoveryAddress')}*</StyledFarmTitle>
            <AddressComponent
                farmAddress={values?.adresse}
                setFarmAddress={(value: string, latlan: Coordinates): void =>
                    setValues({ ...values, adresse: value, coordinates: latlan })
                }
            />
            <StyledFarmTitle variant="body1">{t('methodToRetrieveBasket')}</StyledFarmTitle>
            <InputAreaField
                type="text"
                name="panierRecuperationMethode"
                placeholder={t('methodToRetrieveBasket')}
                value={values?.panierRecuperationMethode}
                onTextChange={(value: string): void => {
                    if (value.length <= 250) {
                        onTextFieldChange('panierRecuperationMethode', value);
                        setWordCount(value.length);
                    }
                }}
                width="645px"
                noOfRows={7}
            />
            <StyledNormalTypography variant="caption" color="#394A35" sx={{ marginBottom: '10px' }}>
                {t('wordCount250', { count: wordCount })}
            </StyledNormalTypography>
            <StyledFarmTitle variant="body1">{t('farmDescription')}</StyledFarmTitle>
            <InputAreaField
                type="text"
                name="infoFerme"
                placeholder={t('farmDescription')}
                value={values?.infoFerme}
                onTextChange={(value: string): void => {
                    if (value.length <= 500) {
                        onTextFieldChange('infoFerme', value);
                        setDescriptionCount(value.length);
                    }
                }}
                width="645px"
                noOfRows={12}
            />
            <StyledNormalTypography variant="caption" color="#394A35" sx={{ marginBottom: '10px' }}>
                {t('wordCount500', { count: descriptionCount })}
            </StyledNormalTypography>
            <StyledFarmTitle variant="body1">{t('farmPhotoIllustration')}</StyledFarmTitle>
            <StyledRowBox>
                <StyledImageBox sx={{ border: basketPhoto ? 'none' : '1px solid #394A35' }}>
                    {!basketPhoto && (
                        <div>
                            <input
                                type="file"
                                ref={basketPhotoFile}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>): void =>
                                    updateBasketPhoto(event)
                                }
                                accept="image/*"
                                multiple={false}
                                style={{ display: 'none' }}
                            />
                            <StyledIconButton
                                onClick={triggerBasketPhotoUpload}
                                sx={{ flexDirection: 'column' }}>
                                <StyledCameraIcon src={cameraIcon} alt="imageUpload" />
                                <StyledMediumTypography variant="caption">
                                    {t('addPicture')}
                                </StyledMediumTypography>
                            </StyledIconButton>
                        </div>
                    )}
                    {basketPhoto && (
                        <div>
                            <StyledImage src={getImageKitUrlFrom(basketPhoto)} alt="imageUpload" />
                            <StyledDeleteLink
                                type="button"
                                onClick={(): void => {
                                    removeBasketPhoto();
                                }}
                                sx={{ marginBottom: '18px' }}>
                                <Typography
                                    variant="overline"
                                    sx={{
                                        textTransform: 'none',
                                        letterSpacing: 0,
                                        fontWeight: 500
                                    }}>
                                    {t('deletePhoto')}
                                </Typography>
                            </StyledDeleteLink>
                        </div>
                    )}
                </StyledImageBox>
            </StyledRowBox>
            <StyledModalButtons sx={{ justifyContent: 'flex-end', margin: '20px 0px 20px 0px' }}>
                <StyledWhiteButton onClick={(): void => navigate('/view-farm')}>
                    <StyledMediumTypography variant="caption">{t('cancel')}</StyledMediumTypography>
                </StyledWhiteButton>
                {loading && (
                    <CircularProgress
                        style={{
                            color: '#134C03',
                            height: '25px',
                            width: '25px',
                            marginTop: '25px'
                        }}
                    />
                )}
                {!loading && (
                    <StyledGreenButton
                        disabled={disabled}
                        sx={{ opacity: disabled ? 0.5 : 1 }}
                        onClick={(): Promise<void> => publishFarm()}>
                        <StyledMediumTypography
                            variant="caption"
                            color="#FFFFFF"
                            sx={{ textTransform: 'uppercase' }}>
                            {t('save')}
                        </StyledMediumTypography>
                    </StyledGreenButton>
                )}
            </StyledModalButtons>
        </StyledBasketBox>
    );
}
